<template>
  <div class="grid-y">
    <LowAvailability :product-id="productId" />
    <Notification
      v-if="error"
      severity="error"
      class="margin-bottom-m"
      aria-live="assertive"
      role="alert"
    >
      {{ error.message }}
    </Notification>

    <UpdateBag
      v-if="isUpdateBagEnabled"
      :payload="atb.payload.value"
      :payload-validator="atb.payloadValidator"
      @error="errorHandler"
    />
    <template v-else>
      <div
        class="cta-buttons"
        :class="{ 'inline': shoppableRedesignOptimization && props.buttonsInline }"
      >
        <AddToBag
          :payload="atb.payload.value"
          :payload-validator="atb.payloadValidator"
          :disabled="autoReplenishmentEnabled && !frequencySelected"
          @error="errorHandler"
        >
          <span> {{ autoReplenishmentEnabled? 'Add To Bag & Subscribe' : 'Add To Bag' }} </span>
        </AddToBag>
        <BuyNow
          v-if="buyNow"
          v-show="!autoReplenishmentEnabled"
          :payload="atb.payload.value"
          :payload-validator="atb.payloadValidator"
          @error="errorHandler"
        />
      </div>
      <div
        v-if="showCallCenterBanner"
        class="margin-top-s"
        v-html="callCenterMessage"
      />
    </template>
    <AddToRegistry
      v-if="addToRegistry"
      :payload="atr.payload.value"
      :payload-validator="atr.payloadValidator"
      @error="errorHandler"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import Notification from '@atomic-ui/notification';

import { useAutoReplenishment } from '@modules/auto-replenishment';
import { LowAvailability } from '@modules/availability';
import {
  AddToBag, BuyNow, UpdateBag, useATBPayload, useBagContext,
} from '@modules/add-to-bag';

import { useProduct } from '@modules/product';
import { useFeatureEligibility, useProductFlags, useTopLevelCategoryFlags } from '@modules/featureControl';
import { AddToRegistry, useATRPayload } from '@modules/add-to-registry';
import { useAvailabilityBigTicket } from '@modules/availability/composables/useAvailabilityBigTicket';

import { useExperiments } from '@composables/useExperiments';
import { useBrand } from '@composables/useBrand';
import messages from '../../constants/messages';

const { isFineJewelryCategory } = useTopLevelCategoryFlags();

/**
 * @type {import('vue').Ref<Nullable<Error>>}
 */
const error = ref(null);

const props = defineProps({
  atbPayload: {
    /** @type {import('vue').PropType<Partial<ATBPayloadItem>>} */
    type: Object,
    default: () => ({}),
  },
  buttonsInline: {
    type: Boolean,
    default: true,
  },
});

const { productId } = useProduct();
const { autoReplenishmentEnabled, frequencySelected } = useAutoReplenishment();
const { callCenterCTAMessage } = useAvailabilityBigTicket();
const { isBigTicketItemFlag } = useProductFlags();

const { addToRegistry, buyNow, shoppableRedesignOptimization } = useFeatureEligibility();
const { isUpdateBagEnabled } = useBagContext();
const { experimentFlags: { dnfPlacementEnabled } } = useExperiments();
const { isBcom } = useBrand();

const atb = useATBPayload({
  additionalPayload: () => props.atbPayload,
});
const atr = useATRPayload();
const showCallCenterBanner = computed(() => (isBigTicketItemFlag && !(isBcom && dnfPlacementEnabled)) || isFineJewelryCategory);
const callCenterMessage = computed(() => (isFineJewelryCategory ? messages.callCenterJewelryMessage : callCenterCTAMessage));

/**
 *
 * @param {Error|any} err
 */
const errorHandler = (err) => {
  error.value = err;
};
</script>
